import React from 'react';
import clsx from 'clsx';
import { Paperclip, X } from 'lucide-react';
import { Button } from './ui';
import {
  formatAttachmentSize,
  getDiscussionAttachmentUrl,
  validateAttachmentFiles
} from '../lib/attachments';
import { createDiscussionAttachmentOpenUrl } from '../lib/cloudFunctions';
import type { DiscussionAttachment } from '../lib/types';

const ACCEPTED_ATTACHMENTS = '.png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf';
const UPLOAD_HINT = 'PNG, JPG/JPEG, PDF - max 10MB';

export function AttachmentPicker(props: {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
}) {
  const [error, setError] = React.useState('');

  function addFiles(fileList: FileList | null) {
    if (!fileList) return;
    const next = [...props.files, ...Array.from(fileList)];
    const validationError = validateAttachmentFiles(next);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    props.onChange(next);
  }

  function removeFile(index: number) {
    const next = props.files.filter((_, fileIndex) => fileIndex !== index);
    setError(validateAttachmentFiles(next));
    props.onChange(next);
  }

  return (
    <AttachmentPickerLayout
      files={props.files}
      disabled={props.disabled}
      error={error}
      onAddFiles={addFiles}
      onRemoveFile={removeFile}
      className="flex min-w-0 flex-wrap items-center justify-end gap-2"
      chipClassName="max-w-[16rem]"
    />
  );
}

export function AttachmentPickerLayout(props: {
  files: File[];
  disabled?: boolean;
  error?: string;
  onAddFiles: (fileList: FileList | null) => void;
  onRemoveFile: (index: number) => void;
  className?: string;
  chipClassName?: string;
  buttonClassName?: string;
  errorClassName?: string;
}) {
  return (
    <div className={props.className ?? 'flex min-w-0 flex-wrap items-center gap-2'}>
      {props.files.map((file, index) => (
        <span
          key={`${file.name}-${file.size}-${index}`}
          className={clsx(
            'inline-flex min-w-0 items-center gap-1 rounded-full border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700',
            props.chipClassName ?? 'max-w-[16rem]'
          )}
          title={`${file.name} (${formatAttachmentSize(file.size)})`}
        >
          <span className="truncate font-medium text-zinc-900">{file.name}</span>
          <span className="shrink-0 text-zinc-500">{formatAttachmentSize(file.size)}</span>
          <button
            type="button"
            onClick={() => props.onRemoveFile(index)}
            disabled={props.disabled}
            className="ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-50"
            aria-label={`Remove ${file.name}`}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </span>
      ))}

      <label
        title={UPLOAD_HINT}
        aria-label={UPLOAD_HINT}
        className={clsx(
          'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-zinc-300 bg-white text-zinc-800 transition hover:bg-zinc-50',
          props.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          props.buttonClassName
        )}
      >
        <Paperclip className="h-4 w-4" aria-hidden />
        <input
          type="file"
          multiple
          accept={ACCEPTED_ATTACHMENTS}
          disabled={props.disabled}
          className="hidden"
          onChange={(event) => {
            props.onAddFiles(event.currentTarget.files);
            event.currentTarget.value = '';
          }}
        />
      </label>

      {props.error ? (
        <div className={props.errorClassName ?? 'w-full text-right text-xs text-red-700'}>{props.error}</div>
      ) : null}
    </div>
  );
}

function AttachmentLink(props: {
  attachment: DiscussionAttachment;
  canDelete?: boolean;
  onDelete?: (attachment: DiscussionAttachment) => void;
  deleting?: boolean;
}) {
  const { attachment } = props;
  const [url, setUrl] = React.useState('');
  const [loadError, setLoadError] = React.useState('');
  const [opening, setOpening] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    let objectUrl = '';
    setUrl('');
    setLoadError('');
    void getDiscussionAttachmentUrl(attachment)
      .then((next) => {
        objectUrl = next;
        if (active) setUrl(next);
        else URL.revokeObjectURL(next);
      })
      .catch((error) => {
        console.error('Failed to load attachment', error);
        if (active) {
          setUrl('');
          setLoadError('Could not retrieve this attachment.');
        }
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, attachment.storagePath]);

  async function openAttachment() {
    if (opening) return;

    const opened = window.open('about:blank', '_blank');
    if (opened) opened.opener = null;
    setOpening(true);
    setLoadError('');
    try {
      const nextOpenUrl = await createDiscussionAttachmentOpenUrl(
        attachment.replyId
          ? {
              kind: 'reply',
              topicId: attachment.topicId,
              replyId: attachment.replyId,
              attachmentId: attachment.id
            }
          : {
              kind: 'topic',
              topicId: attachment.topicId,
              attachmentId: attachment.id
            }
      );

      if (opened) {
        opened.location.href = nextOpenUrl;
      } else {
        window.location.assign(nextOpenUrl);
      }
    } catch (error) {
      console.error('Failed to open attachment', error);
      if (opened) opened.close();
      setLoadError('Could not open this attachment.');
    } finally {
      setOpening(false);
    }
  }

  const isImage = attachment.contentType === 'image/png' || attachment.contentType === 'image/jpeg';
  const actionClass =
    'inline-flex shrink-0 items-center justify-center rounded-xl bg-zinc-900 px-3 py-2 text-sm font-medium text-white hover:bg-zinc-800';
  const disabledActionClass =
    'inline-flex shrink-0 items-center justify-center rounded-xl bg-zinc-200 px-3 py-2 text-sm font-medium text-zinc-500';
  const actions = (
    <div className="flex shrink-0 items-center gap-2">
      <button
        type="button"
        onClick={() => void openAttachment()}
        disabled={opening}
        className={opening ? disabledActionClass : actionClass}
      >
        {opening ? 'Opening...' : 'Open'}
      </button>
    </div>
  );

  const content = isImage ? (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50">
      {url ? (
        <img src={url} alt={attachment.fileName} className="max-h-64 w-full object-contain" loading="lazy" />
      ) : loadError ? (
        <div className="p-3 text-sm text-red-700">{loadError}</div>
      ) : (
        <div className="p-3 text-sm text-zinc-600">Loading image</div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-zinc-200 px-3 py-2">
        <div className="min-w-0 text-xs text-zinc-600">
          <span className="font-medium text-zinc-800">{attachment.fileName}</span> · {formatAttachmentSize(attachment.sizeBytes)}
        </div>
        {actions}
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-zinc-900">{attachment.fileName}</div>
        <div className={loadError ? 'text-xs text-red-700' : 'text-xs text-zinc-600'}>
          {loadError || `PDF · ${formatAttachmentSize(attachment.sizeBytes)}`}
        </div>
      </div>
      {actions}
    </div>
  );

  return (
    <div className="space-y-2">
      {content}
      {props.canDelete ? (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            onClick={() => props.onDelete?.(attachment)}
            disabled={props.deleting}
          >
            {props.deleting ? 'Deleting...' : 'Delete attachment'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function AttachmentList(props: {
  attachments: DiscussionAttachment[];
  canDelete?: boolean;
  deletingAttachmentId?: string;
  onDelete?: (attachment: DiscussionAttachment) => void;
}) {
  if (!props.attachments.length) return null;

  return (
    <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
      {props.attachments.map((attachment) => (
        <AttachmentLink
          key={attachment.id}
          attachment={attachment}
          canDelete={props.canDelete}
          onDelete={props.onDelete}
          deleting={props.deletingAttachmentId === attachment.id}
        />
      ))}
    </div>
  );
}
